import { BigNumber } from "bignumber.js";

import { OPERATOR_GLYPH_TO_INFIX_KIND, OPERATOR_SYMBOL_TO_INFIX_KIND } from "../mappings";
import { getScalarRatioDivisor, unitInlineChildrenToSemantic } from "../unitToSympyMap";
import { encodeDecoratedName } from "../utils";

import type { SympyBuilder } from "./SympyBuilder";
import type { InfixKind } from "../mappings";
import type { SemanticExpr } from "../types";
import type { NodeJson } from "@corca/data";

export class SympyBuildError extends Error {}

/* -------------------------------------------------------------------------- */
/* SemanticExpr construction helpers                                          */
/* -------------------------------------------------------------------------- */

export function op(name: string, args: (SemanticExpr | SemanticExpr[])[]): SemanticExpr {
  return { type: "operator", name, args } as SemanticExpr;
}

export function fn(name: string, args: SemanticExpr[]): SemanticExpr {
  return { type: "function", name, args };
}

export function numberExpr(value: string): SemanticExpr {
  return { type: "number", kind: BigNumber(value).isInteger() ? "integer" : "float", value };
}

export function constantExpr(sympy: string): SemanticExpr {
  switch (sympy) {
    case "Pi":
      return op("Pi", []);
    case "Exp1":
      return op("Exp1", []);
    case "I":
      return { type: "imaginary_unit" };
    case "oo":
      return { type: "infinity", kind: "positive", value: Infinity };
    case "GoldenRatio":
      return op("GoldenRatio", []);
    case "Catalan":
      return op("Catalan", []);
    case "EmptySet":
      return op("EmptySet", []);
    default:
      throw new SympyBuildError(`Unknown constant: ${sympy}`);
  }
}

/* -------------------------------------------------------------------------- */
/* Node helpers (ported from buildSympyCode.ts)                               */
/* -------------------------------------------------------------------------- */

/** Divisor if a number node carries a scalar-ratio unit child (`%`, `‰`, `‱`), else null. */
export function getNumberScalarRatioDivisor(node: NodeJson): number | null {
  if (node.type !== "number") return null;
  const unitChild = node.children.find((c) => c.type === "unit");
  const inlineChild = unitChild?.children.find((c) => c.type === "inline");
  if (!inlineChild || inlineChild.children.length !== 1) return null;
  const only = inlineChild.children[0];
  if (only.type !== "plainText") return null;
  return getScalarRatioDivisor(only.props.value);
}

/** Extract plain text from a NodeJson tree, matching TextVisitor behavior. */
export function nodeJsonPlainText(node: NodeJson): string {
  if ("value" in node.props && node.props.value != null) return String(node.props.value);
  return node.children.map(nodeJsonPlainText).join("");
}

/** Depth-first search for the first NodeJson matching `predicate`. */
export function findFirstNodeJson(
  predicate: (n: NodeJson) => boolean,
  node: NodeJson,
): NodeJson | null {
  if (predicate(node)) return node;
  for (const child of node.children) {
    const found = findFirstNodeJson(predicate, child);
    if (found) return found;
  }
  return null;
}

/**
 * Extract the bound variable name from a Sum/Product/Integral bottom label.
 * Handles "i=0" (Eq function), "i=0" raw operator-symbol form, and bare "i".
 */
export function extractBoundVarName(bottomLabelInline: NodeJson | undefined): string | null {
  if (!bottomLabelInline || bottomLabelInline.type !== "inline") return null;
  const found = findFirstNodeJson(
    (n) =>
      n.type === "variable" &&
      n.children[0]?.type === "symbol" &&
      typeof n.children[0].props.value === "string" &&
      n.children[0].props.value.length > 0,
    bottomLabelInline,
  );
  if (!found || found.children[0].type !== "symbol") return null;
  const { value } = found.children[0].props;
  return typeof value === "string" ? value : null;
}

export function getLabeledVariableName(node: NodeJson): string | null {
  if (node.type !== "variable" && node.type !== "constant") return null;
  if (node.children[0].type !== "symbol") return null;
  const symbolNode = node.children[0];
  const varName = symbolNode.props.value;
  if (!varName) return null;

  const bottomLabels = symbolNode.children.filter(
    (n) => n.type === "label" && n.props.position === "right-bottom",
  );
  const topLabels = symbolNode.children.filter(
    (n) => n.type === "label" && n.props.position === "right-top",
  );

  const labelStr = bottomLabels.flatMap((l) => l.children.map(nodeJsonPlainText)).join("");
  const supStr = topLabels.flatMap((l) => l.children.map(nodeJsonPlainText)).join("");
  let baseName = labelStr ? `${varName}_${labelStr}` : varName;
  if (supStr) {
    baseName = `${baseName}__sup_${supStr}`;
  }

  const decoratorNames = symbolNode.children
    .filter((n) => n.type === "decorator")
    .map((n) => n.props.name as string)
    .filter((name) => typeof name === "string");

  return encodeDecoratedName(baseName, decoratorNames);
}

/**
 * Non-recursive check: returns true if a power operator's exponent is exactly
 * the symbol-transpose symbol (⊺).
 */
export function isTransposeExponent(powerNode: NodeJson): boolean {
  const exponentInline = powerNode.children[0];
  if (!exponentInline || exponentInline.children.length !== 1) return false;
  const child = exponentInline.children[0];
  if (child.type === "symbol" && child.props.key === "symbol-transpose") return true;
  if (
    child.type === "operatorSymbol" &&
    child.children[0]?.type === "symbol" &&
    child.children[0].props.key === "symbol-transpose"
  )
    return true;
  return false;
}

/**
 * Recursively checks if a node or any of its children is a real matrix node.
 * 1x1 matrices are parenthesized scalar expressions, not actual matrices.
 */
export function containsMatrixNode(node: NodeJson): boolean {
  if (node.type === "matrix") {
    return (node.props.rows ?? 1) * (node.props.cols ?? 1) > 1;
  }
  return node.children.some((child) => containsMatrixNode(child));
}

/**
 * True if any number leaf in the semantic tree is a non-integer float — i.e. the user
 * typed a decimal. Extracted as builder meta so the result renderer can prefer decimals.
 */
export function semanticHasFloat(expr: SemanticExpr): boolean {
  switch (expr.type) {
    case "number":
      return expr.kind === "float";
    case "operator":
    case "function":
      return expr.args.some((arg) =>
        Array.isArray(arg) ? arg.some(semanticHasFloat) : semanticHasFloat(arg),
      );
    case "matrix":
      return expr.data.some(semanticHasFloat);
    case "plus_minus":
      return semanticHasFloat(expr.real) || semanticHasFloat(expr.imag);
    default:
      return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Operator-symbol classification (for inline folding)                        */
/* -------------------------------------------------------------------------- */

/** Prefers the symbol-key; the glyph fallback is for nodes that carry no key, and warns when hit. */
export function classifyInfix(builder: SympyBuilder, node: NodeJson): InfixKind {
  const symbolNode = node.children[0]?.type === "symbol" ? node.children[0] : null;

  const byKey = OPERATOR_SYMBOL_TO_INFIX_KIND[symbolNode?.props.key ?? ""];
  if (byKey) return byKey;

  const glyph = symbolNode?.props.value ?? "";
  const byGlyph = OPERATOR_GLYPH_TO_INFIX_KIND[glyph];
  if (byGlyph) {
    builder.warn(
      `Operator symbol "${glyph}" classified by glyph — key ${
        symbolNode?.props.key ? `"${symbolNode.props.key}" is unmapped` : "is missing"
      }.`,
    );
    return byGlyph;
  }

  return "other";
}

/** The glyph an operator symbol renders as (`∪`, `→`, …) — for matching and error messages. */
export function operatorSymbolString(node: NodeJson): string {
  const symbolNode = node.children[0]?.type === "symbol" ? node.children[0] : null;
  return symbolNode?.props.value ?? "";
}

export type FactorToken = { kind: "factor"; expr: SemanticExpr } | { kind: "div" };

export type TermSign = "plus" | "minus";

/** True if every unit in the node tree is a known SI unit. */
export function allUnitsAreSi(node: NodeJson): boolean {
  if (node.type === "unit") {
    const inlineChild = node.children.find((c) => c.type === "inline");
    if (!inlineChild || inlineChild.children.length === 0) return true;
    return unitInlineChildrenToSemantic(inlineChild.children) !== null;
  }
  return node.children.every((child) => allUnitsAreSi(child));
}
